import { useOfflineSync } from "hooks/useOfflineSync";
import { usePWA } from "hooks/usePWA";
import moment from "moment";
import { Button } from "components";
import { useOfflineStorage } from "hooks/useOfflineStorage";
import { getOfflineQueue } from "hooks/useOfflineQueue";
import { syncOfflineQueue } from "hooks/useOfflineQueueSync";
import { Toast } from "components";
import { TOAST_TYPES_ENUM } from "enums";
import "./synchronization-offline.scss";
import { useEffect, useState } from "react";

type OfflineSectionId =
  | "userData"
  | "measurementConfig"
  | "calendar"
  | "ordersList"
  | "ordersDetails";

type OfflineSectionStatus = "ok" | "partial" | "missing";

type OfflineSection = {
  id: OfflineSectionId;
  label: string;
  description: string;
  critical: boolean;
  status: OfflineSectionStatus;
  presentCount: number;
  requiredCount: number;
  details?: {
    label: string;
    key: string;
    present: boolean;
    hasDetails?: boolean;
    hasTime?: boolean;
  }[];
};

const measurementConfigEndpoints = [
  "types-of-measurements",
  "rooms",
  "window-addition",
  "raw-condition",
  "types-of-installation?types=windowsInRawCondition",
  "types-of-installation?types=windowsReplacement",
  "types-of-installation?types=externalDoorsInRawCondition",
  "types-of-blinds-installation",
  "construction-sites?types=rollerBlindsAndBlinds",
  "construction-sites?types=windowsInRawCondition",
  "construction-sites?types=windowsReplacement",
  "installation-elements?types=windowsInRawCondition",
  "installation-elements?types=garageDoor",
  "installation-elements?types=rollerBlindsAndBlinds",
  "installation-elements?types=windowsReplacement",
];

const measurementConfigLabels: Record<string, string> = {
  "types-of-measurements": "Rodzaje pomiarów",
  rooms: "Lista pomieszczeń",
  "window-addition": "Dodatki do okna",
  "raw-condition": "Warunki montażu (stan surowy)",

  "types-of-installation?types=windowsInRawCondition":
    "Typy montażu – okna stan surowy",
  "types-of-installation?types=windowsReplacement":
    "Typy montażu – okna wymiana",
  "types-of-installation?types=externalDoorsInRawCondition":
    "Typy montażu – drzwi zewnętrzne stan surowy",

  "types-of-blinds-installation": "Typy montażu – rolety i żaluzje",

  "construction-sites?types=rollerBlindsAndBlinds":
    "Miejsca montażu – rolety i żaluzje (droga dojazdowa itp.)",
  "construction-sites?types=windowsInRawCondition":
    "Miejsca montażu – okna stan surowy (droga dojazdowa itp.)",
  "construction-sites?types=windowsReplacement":
    "Miejsca montażu – okna wymiana (droga dojazdowa itp.)",

  "installation-elements?types=windowsInRawCondition":
    "Elementy instalacyjne – okna stan surowy",
  "installation-elements?types=garageDoor":
    "Elementy instalacyjne – bramy garażowe",
  "installation-elements?types=rollerBlindsAndBlinds":
    "Elementy instalacyjne – rolety i żaluzje",
  "installation-elements?types=windowsReplacement":
    "Elementy instalacyjne – okna wymiana",
};

const SynchronizationOffline = () => {
  const {
    syncAllData,
    isSyncing,
    lastSyncTime,
    todayOrdersSyncedCount,
    todayOrdersDetails,
    todayOrdersTotalFromCalendar,
  } = useOfflineSync();
  const { getCacheInfo } = useOfflineStorage();

  const { isOffline } = usePWA();
  const [expandedSectionId, setExpandedSectionId] =
    useState<OfflineSectionId | null>(null);
  const [cacheInfo, setCacheInfo] = useState(() => getCacheInfo());
  const [pendingOperationsCount, setPendingOperationsCount] =
    useState<number>(0);
  const [isSendingQueue, setIsSendingQueue] = useState<boolean>(false);

  const getEffectivePendingCount = () => {
    const operations = getOfflineQueue().filter(
      (op) => op.status === "pending" || op.status === "failed"
    );

    const idsToSkip = new Set<string>();
    const measurementOps = operations.filter(
      (op) => op.entityType === "measurement"
    );

    for (const postOp of measurementOps) {
      if (postOp.method !== "POST" || !postOp.tempId) continue;

      const tempId = postOp.tempId;

      const matchingDelete = measurementOps.find((op) => {
        if (op.method !== "DELETE") return false;
        const url = op.url || "";
        const uuidParam = "measurementUuid=";
        const idx = url.indexOf(uuidParam);
        if (idx === -1) return false;
        const value = decodeURIComponent(url.slice(idx + uuidParam.length));
        return value === tempId;
      });

      if (matchingDelete) {
        idsToSkip.add(postOp.id);
        idsToSkip.add(matchingDelete.id);
      }
    }

    const effectiveCount = operations.filter(
      (op) => !idsToSkip.has(op.id)
    ).length;

    return effectiveCount;
  };

  useEffect(() => {
    const initialPending = getEffectivePendingCount();

    setPendingOperationsCount(initialPending);
  }, []);

  useEffect(() => {
    const updatedCacheInfo = getCacheInfo();
    setCacheInfo(updatedCacheInfo);
    const pending = getEffectivePendingCount();
    setPendingOperationsCount(pending);
  }, [lastSyncTime, getCacheInfo]);

  const lastSyncFormatted = moment(lastSyncTime).format("DD.MM.YYYY HH:mm");
  const isLastSync = lastSyncFormatted !== "Invalid date" || false;
  const lastSyncLabel = isLastSync
    ? `Ostatnia synchronizacja: ${lastSyncFormatted}`
    : "Brak wykonanej synchronizacji – przed wyjazdem pobierz dane offline.";

  const cacheKeys = cacheInfo.cachedUrls;
  const countPresent = (requiredKeys: string[]) => {
    return requiredKeys.filter((key) => cacheKeys.includes(key)).length;
  };

  const resolveStatus = (
    present: number,
    required: number
  ): OfflineSectionStatus => {
    if (required === 0) return "missing";
    if (present === 0) return "missing";
    if (present === required) return "ok";
    return "partial";
  };

  const userDataKeys = ["users/me", "phonePrefixes/label"];
  const userDataPresent = countPresent(userDataKeys);

  const measurementConfigPresent = countPresent(measurementConfigEndpoints);

  const measurementConfigDetails = measurementConfigEndpoints.map((key) => ({
    key,
    label: measurementConfigLabels[key] || key,
    present: cacheKeys.includes(key),
  }));

  const calendarKeys = cacheKeys.filter((key) =>
    key.startsWith("orders/date/measurer?")
  );
  const calendarPresent = calendarKeys.length;

  const hasOrderDetails = (uuid: string) =>
    cacheKeys.includes(`orders/${uuid}`);

  const hasOrderTimeManagement = (uuid: string) =>
    cacheKeys.includes(`activity/time-management?orderUuid=${uuid}`);

  const sections: OfflineSection[] = [
    {
      id: "userData",
      label: "Dane użytkownika",
      description:
        "Informacje o Twoim koncie i ustawieniach niezbędne do działania aplikacji.",
      critical: true,
      requiredCount: userDataKeys.length,
      presentCount: userDataPresent,
      status: resolveStatus(userDataPresent, userDataKeys.length),
    },
    {
      id: "calendar",
      label: "Kalendarz i plan pracy",
      description: "Zlecenia z Twojego kalendarza na najbliższe pół roku",
      critical: false,
      requiredCount: 6,
      presentCount: calendarPresent,
      status: resolveStatus(calendarPresent, 6),
    },
    {
      id: "ordersList",
      label: "Lista zleceń",
      description: "Zlecenia przypisane do Ciebie pobrane do trybu offline.",
      critical: true,
      requiredCount: todayOrdersTotalFromCalendar || 0,
      presentCount: todayOrdersSyncedCount,
      status: resolveStatus(
        todayOrdersSyncedCount,
        todayOrdersTotalFromCalendar || 0
      ),
      details:
        todayOrdersDetails && todayOrdersDetails.length > 0
          ? todayOrdersDetails.map((order: any) => ({
              key: order.uuid,
              label: order.name || order.title || order.uuid,
              present: true,
              hasDetails: hasOrderDetails(order.uuid),
              hasTime: hasOrderTimeManagement(order.uuid),
            }))
          : undefined,
    },
    {
      id: "measurementConfig",
      label: "Konfiguracja pomiarów",
      description:
        "Typy pomiarów, pokoje, typy montażu i inne słowniki potrzebne do wypełniania formularza pomiaru.",
      critical: true,
      requiredCount: measurementConfigEndpoints.length,
      presentCount: measurementConfigPresent,
      status: resolveStatus(
        measurementConfigPresent,
        measurementConfigEndpoints.length
      ),
      details: measurementConfigDetails,
    },
  ];

  const criticalSections = sections.filter((section) => section.critical);
  const hasMissingCritical = criticalSections.some(
    (section) => section.status === "missing"
  );

  const overallStatus: OfflineSectionStatus = hasMissingCritical
    ? "missing"
    : criticalSections.some((section) => section.status === "partial")
    ? "partial"
    : "ok";

  return (
    <div className="sync-offline">
      <div className="sync-offline__header">
        <div className="sync-offline__top">
          <h1>Przygotowanie do pracy offline</h1>

          <Button
            size="small"
            disabled={isSyncing || isOffline}
            label={
              isOffline
                ? "Tryb offline"
                : isSyncing
                ? "Synchronizuję dane..."
                : "Synchronizuj dane"
            }
            onClick={() => {
              syncAllData();
            }}
          />
        </div>
        <p className="sync-offline__status-label">
          Status przygotowania:{" "}
          <span
            className={`sync-offline__status sync-offline__status--${overallStatus}`}
          >
            {overallStatus === "ok" && "Gotowy"}
            {overallStatus === "partial" && "Częściowo gotowy"}
            {overallStatus === "missing" && "Nie gotowy"}
          </span>
        </p>

        <p className="sync-offline__last-sync">{lastSyncLabel}</p>
      </div>

      <div className="sync-offline__sections">
        {sections.map((section) => {
          const isUserData = section.id === "userData";
          const isCalendar = section.id === "calendar";
          const isExpanded = expandedSectionId === section.id;

          return (
            <div key={section.id} className={`sync-offline__section `}>
              {isUserData || isCalendar ? (
                <div className="sync-offline__section-header">
                  <span className={`sync-offline__badge `}>
                    {section.status === "ok" && "✅"}
                    {section.status === "partial" && "⚠️"}
                    {section.status === "missing" && "❌"}
                  </span>
                  <div>
                    <h2 className="sync-offline__section-title">
                      {section.label}
                    </h2>
                    <p className="sync-offline__section-description">
                      {section.description}
                    </p>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="sync-offline__section-header"
                  onClick={() =>
                    setExpandedSectionId((current) =>
                      current === section.id ? null : section.id
                    )
                  }
                >
                  <span className={`sync-offline__badge `}>
                    {section.status === "ok" && "✅"}
                    {section.status === "partial" && "⚠️"}
                    {section.status === "missing" && "❌"}
                  </span>
                  <div>
                    <h2 className="sync-offline__section-title">
                      {section.label}
                    </h2>
                    <p className="sync-offline__section-description">
                      {section.description}
                    </p>
                  </div>
                  <span className="sync-offline__section-toggle">
                    {isExpanded ? "−" : "+"}
                  </span>
                </button>
              )}

              <p className="sync-offline__section-summary">
                {isUserData
                  ? section.presentCount > 0
                    ? "Dane pobrane."
                    : "Brak danych w pamięci offline."
                  : section.id === "ordersList"
                  ? todayOrdersSyncedCount > 0
                    ? `Dzisiejsze zlecenia zsynchronizowane: ${todayOrdersSyncedCount}/${todayOrdersTotalFromCalendar}`
                    : todayOrdersTotalFromCalendar > 0
                    ? `Znaleziono dzisiejsze zlecenia w kalendarzu: ${todayOrdersTotalFromCalendar}, ale nie udało się pobrać ich danych offline.`
                    : "Brak zsynchronizowanych zleceń na dziś."
                  : section.requiredCount > 1
                  ? `Pobrane: ${section.presentCount}/${section.requiredCount}`
                  : section.presentCount > 0
                  ? "Dane pobrane."
                  : "Brak danych w pamięci offline."}
              </p>
              {section.details && expandedSectionId === section.id && (
                <ul className="sync-offline__section-details">
                  {section.details.map((detail) => (
                    <li
                      key={detail.key}
                      className={`sync-offline__section-detail sync-offline__section-detail--${
                        detail.present ? "ok" : "missing"
                      }`}
                    >
                      {}
                      <div className="sync-offline__section-detail-main">
                        <span className="sync-offline__section-detail-label">
                          {detail.label}
                        </span>
                        <span className="sync-offline__section-detail-status">
                          {detail.present ? "✅" : "❌"}
                        </span>
                      </div>

                      {section.id === "ordersList" && (
                        <div className="sync-offline__section-detail-sub">
                          <div>
                            <span>Szczegóły zlecenia </span>
                            <span>{detail.hasDetails ? "" : "❌"}</span>
                          </div>
                          <div>
                            <span>Raportowanie czasu </span>
                            <span>{detail.hasTime ? "" : "❌"}</span>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {pendingOperationsCount === 0 ? null : (
        <div className="sync-offline__queue">
          <div className="sync-offline__queue-header">
            <h2>Operacje oczekujące na wysłanie</h2>
            <Button
              size="small"
              disabled={isOffline || isSendingQueue}
              label={
                isOffline
                  ? "Tryb offline"
                  : isSendingQueue
                  ? "Wysyłam..."
                  : `Wyślij (${pendingOperationsCount})`
              }
              onClick={async () => {
                if (!navigator.onLine) {
                  return;
                }
                if (isSendingQueue) return;

                setIsSendingQueue(true);
                const toastId = "offline-queue-sync";
                Toast({
                  id: toastId,
                  type: TOAST_TYPES_ENUM.LOADING,
                  message:
                    "Trwa wysyłka danych do serwera. Może to chwilę potrwać...",
                });

                try {
                  await syncOfflineQueue();
                  const pendingAfter = getEffectivePendingCount();
                  setPendingOperationsCount(pendingAfter);

                  Toast({
                    id: toastId,
                    type: TOAST_TYPES_ENUM.SUCCESS,
                    message:
                      pendingAfter === 0
                        ? "Wysyłka zakończona. Wszystkie dane zostały wysłane."
                        : `Wysyłka zakończona. Pozostało w kolejce: ${pendingAfter}.`,
                  });
                } catch (e) {
                  Toast({
                    id: toastId,
                    type: TOAST_TYPES_ENUM.ERROR,
                    message:
                      "Nie udało się wysłać danych. Spróbuj ponownie za chwilę.",
                  });
                } finally {
                  setIsSendingQueue(false);
                }
              }}
            />
          </div>
          <p className="sync-offline__queue-info">
            Aktualnie w kolejce: <strong>{pendingOperationsCount}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default SynchronizationOffline;
